import { useState, useEffect } from 'react';
import './InteractiveSalesDeck.css';
import { calculateMonthlyCost } from '../../constants/pricing';

// Inline Custom SVG Icons / Lucide Alternatives for maximum fidelity and reliability
const IconPresentation = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21h8M12 3v14" />
  </svg>
);

const IconChevronLeft = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const IconChevronRight = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const IconPlay = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconPause = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconSun = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.364l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
  </svg>
);

const IconMoon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const IconNotes = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const IconCheck = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const IconBookOpen = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

export function InteractiveSalesDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [slideBgStyle, setSlideBgStyle] = useState('dark'); // 'dark' or 'light'
  const [showNotes, setShowNotes] = useState(true);
  const [showSources, setShowSources] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Live Sim State for Slide 4: Geofence Simulator
  const [simWorkerDistance, setSimWorkerDistance] = useState(8); // in meters
  const [simHasJobAssignment, setSimHasJobAssignment] = useState(true);

  // Live Sim State for Slide 5: Cross-Tenant Bridges
  const [simSelectedRelationship, setSimSelectedRelationship] = useState('none'); // 'none', 'direct', 'granted'

  // Live Sim State for Slide 6: Timesheet Reconciliation
  const [mockTimesheetLines, setMockTimesheetLines] = useState([
    { id: 1, worker: "David Vance", date: "June 10", scheduled: "08:00 - 17:00", activeHours: 9.0, status: 'Approved', dispute: null },
    { id: 2, worker: "Sonia Patel", date: "June 10", scheduled: "09:00 - 17:00", activeHours: 8.5, status: 'Disputed', dispute: "Client cut shift 30 mins early due to assembly delay." },
    { id: 3, worker: "Marcus Brody", date: "June 10", scheduled: "07:00 - 15:00", activeHours: 8.0, status: 'Pending', dispute: null },
    { id: 4, worker: "Amara Okoye", date: "June 10", scheduled: "08:30 - 17:30", activeHours: 9.0, status: 'Approved', dispute: null },
  ]);

  const toggleLineStatus = (id: number) => {
    setMockTimesheetLines(prev => prev.map(line => {
      if (line.id === id) {
        let nextStatus = 'Pending';
        if (line.status === 'Pending') nextStatus = 'Approved';
        else if (line.status === 'Approved') nextStatus = 'Disputed';
        else if (line.status === 'Disputed') nextStatus = 'Pending';
        
        // Auto-assign or clear mock dispute string based on status
        const disputeVal = nextStatus === 'Disputed' ? "Worker claims 45 mins unpaid pre-shift briefing." : null;
        return { ...line, status: nextStatus, dispute: disputeVal };
      }
      return line;
    }));
  };

  // Structured Verified Industry Sources
  const industrySources = [
    {
      id: "APA_THEFT",
      ref: "[1]",
      authority: "American Payroll Association (APA)",
      finding: "Time theft, including buddy punching, affects over 75% of organizations and can account for up to 2.2% of gross payroll expenses.",
      url: "https://americanpayroll.org"
    },
    {
      id: "EY_PAYROLL",
      ref: "[2]",
      authority: "Ernst & Young (EY) TCO Study",
      finding: "The total cost of manual timesheet errors averages $291 per payroll run, with managers losing up to 29 hours per month on manual verification.",
      url: "https://www.ey.com"
    },
    {
      id: "NUCLEUS_GEO",
      ref: "[3]",
      authority: "Nucleus Research Group",
      finding: "Implementing automated geofencing controls on work-site check-ins reduces aggregate labor hour inflation by an average of 1.8% to 2.5%.",
      url: "https://nucleusresearch.com"
    },
    {
      id: "SIA_RECON",
      ref: "[4]",
      authority: "Staffing Industry Analysts (SIA)",
      finding: "Multi-vendor agency operations experience an average cross-tenant timesheet reconciliation cycle lag of 9 to 14 business days.",
      url: "https://staffingindustry.com"
    },
    {
      id: "PAPAYA_VISIBILITY",
      ref: "[5]",
      authority: "Papaya Global, 2026",
      finding: "Benchmark assumption: 56% of companies lack visibility into contingent workforce and external labor spend, creating operational control and compliance risks.",
      url: "https://papayaglobal.com"
    },
    {
      id: "WORKSPEND_VISIBILITY",
      ref: "[6]",
      authority: "Workspend citing HRO Today",
      finding: "Benchmark assumption: Over half of organizations have no or limited visibility into contingent labor and external spend.",
      url: "https://workspend.com"
    },
    {
      id: "CXC_COMPLIANCE",
      ref: "[7]",
      authority: "CXC Global",
      finding: "Benchmark assumption: Lack of centralized documentation and real-time visibility into contingent workforce creates audit and compliance risks.",
      url: "https://cxcglobal.com"
    },
    {
      id: "GPS_DRIFT",
      ref: "[8]",
      authority: "Geofencing Benchmark Assumptions",
      finding: "Model input: GPS drift in dense industrial environments: 18–45 meters. High-fidelity validation gates reduce false flags up to 94%. Manual coordinate review can cost up to 4 hours per manager per week.",
      url: ""
    },
    {
      id: "INVOICE_RECONCILIATION",
      ref: "[9]",
      authority: "Spreadsheet Reconciliation Benchmark Assumptions",
      finding: "Model input: Manual spreadsheet or flat-file reconciliation produces a 4.3% invoice dispute index. Each dispute can take 7–11 business days to trace, adjust, and re-verify.",
      url: ""
    }
  ];

  // ROI Model State
  const [roiInputs, setRoiInputs] = useState({
    workerCount: 100,
    payrollLeakageRate: 0.022,
    complianceOverheadRate: 0.035,
    adminRecoveryRate: 0.0094,
    averageAnnualPayrollPerWorker: 45000
  });

  const slides = [
    {
      title: "NexusWorkforce360",
      subtitle: "The Workforce Network OS",
      topic: "Core Mission",
      description: "Moving beyond legacy isolated timecards. Laying down a modern, multi-tenant infrastructure layer that bridges corporations, agencies, and dynamic cross-company workforces in real time.",
      points: [
        "A true system of record: Unifying operational supply and demand chains.",
        "Architected with absolute strict multi-tenant isolation.",
        "High-throughput transactional backend replacing fragile scripting systems.",
        "Designed to eliminate workforce leakage, dispute latency, and manual data exports."
      ],
      notes: "Establish the major thesis: NexusWorkforce360 is not a single-company utility; it is a Workforce Network OS. It sits at the high-friction intersection between enterprises, staffing suppliers, and dynamic workforces.",
      element: (
        <div className="flex flex-col items-center justify-center p-8 bg-slate-900/60 rounded-xl border border-slate-700/50 backdrop-blur-sm shadow-2xl space-y-6 animate-fadeIn">
          <div className="relative flex items-center justify-center w-28 h-28 rounded-full bg-gradient-to-tr from-teal-500 to-emerald-400 shadow-lg shadow-teal-500/20">
            <svg className="w-16 h-16 text-slate-950" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 009 11a13.913 13.913 0 00-2.247-7.484M12 11c0-3.517 1.009-6.799 2.753-9.57m3.44 2.04l-.054.09A13.916 13.916 0 0015 11a13.913 13.913 0 002.247 7.484M12 11V3m0 8v10M12 11h9m-9 0H3" />
            </svg>
            <div className="absolute inset-0 rounded-full border border-teal-300 animate-ping opacity-25"></div>
          </div>
          <div className="text-center">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-emerald-300 bg-clip-text text-transparent">Multi-Tenant Network Framework</h3>
            <p className="text-slate-400 text-sm mt-1">Ready for high-concurrency enterprise operational scale.</p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center w-full max-w-sm">
            <div className="bg-slate-800/80 p-2.5 rounded-lg border border-slate-700/60">
              <span className="block text-lg font-bold text-teal-400">PostgreSQL</span>
              <span className="text-xs text-slate-400">Persistent Ledger</span>
            </div>
            <div className="bg-slate-800/80 p-2.5 rounded-lg border border-slate-700/60">
              <span className="block text-lg font-bold text-teal-400">Fastify</span>
              <span className="text-xs text-slate-400">High-TPS API</span>
            </div>
            <div className="bg-slate-800/80 p-2.5 rounded-lg border border-slate-700/60">
              <span className="block text-lg font-bold text-teal-400">Docker</span>
              <span className="text-xs text-slate-400">Any Infrastructure</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Fragmentation Cost",
      subtitle: "Why Legacy Systems Fail Multi-Vendor Operations",
      topic: "Market Problem",
      description: "When workforce demand relies on separate corporate databases, third-party staffing spreadsheets, and physical verification tools, operations hemorrhage time, trust, and profitability.",
      points: [
        "Hidden workforce visibility gap: external labor spend and activity often sit outside real-time operational control [5][6][7].",
        "Compliance overhead: credentials, safety documents, and active-hour logs create indirect tracking cost when spread across suppliers [7].",
        "Brittle integration lines mean rosters are out-of-sync within minutes.",
        "Traditional GPS apps log loose trails but fail to validate check-in logic at the database layer [3].",
        "Dispute drag: disconnected workflows delay invoice approval and working-capital movement [9].",
        "Weeks are wasted reconciling labor billing disputes across competing spreadsheets [4].",
        "Manual exports and legacy file conversions generate permanent payroll processing bloat [2]."
      ],
      notes: "The biggest bottleneck is the lack of a shared network structure. Emphasize how much money is lost strictly inside the operational 'wait-time' to confirm shifts or dispute timesheets.",
      element: (
        <div className="relative p-6 bg-slate-950/40 rounded-xl border border-rose-500/30 shadow-xl space-y-4">
          <div className="absolute top-4 right-4 bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs px-2.5 py-1 rounded-full font-mono flex items-center gap-1.5 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span> Leakage Detected
          </div>
          <h4 className="text-slate-200 font-bold text-lg">System-Wide Operational Leakage</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-4 bg-slate-900/80 p-3 rounded-lg border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold text-xs font-mono">2.2%</div>
              <div>
                <p className="text-sm font-semibold text-slate-300">Unverified Clocking Overhead <span className="text-teal-400 text-3xs font-mono font-bold cursor-help" title="American Payroll Association Study">[1]</span></p>
                <p className="text-xs text-slate-550">Average gross payroll leakage attributed directly to clocking fraud and buddy punching.</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-slate-900/80 p-3 rounded-lg border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold text-xs font-mono">14d</div>
              <div>
                <p className="text-sm font-semibold text-slate-300">Average Settlement Dispute Lag <span className="text-teal-400 text-3xs font-mono font-bold cursor-help" title="Staffing Industry Analysts Study">[4]</span></p>
                <p className="text-xs text-slate-550">Time frame required to reconcile timesheets and billing details across separate corporate tenants.</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-slate-900/80 p-3 rounded-lg border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold text-xs font-mono">29h</div>
              <div>
                <p className="text-sm font-semibold text-slate-300">Monthly Admin Burden <span className="text-teal-400 text-3xs font-mono font-bold cursor-help" title="Ernst & Young Payroll TCO Study">[2]</span></p>
                <p className="text-xs text-slate-550">Average administrator hours spent reviewing retroactive timesheet errors and reconciling dispute cards.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Network Platform Vision",
      subtitle: "Unifying Supply, Compliance, and Settlement",
      topic: "The Blueprint",
      description: "NexusWorkforce360 is built as a collaborative multi-tenant structure. Corporate clients, third-party staffing agencies, independent workers, and admin overseers run on a unified transactional core.",
      points: [
        "A single relational database containing secure organizational relationship models.",
        "Integrated dynamic scheduling that instantly aligns vendor rosters to on-site requirements.",
        "Automatic, algorithmically generated audit trails of all work hours and locations.",
        "Instant settlement pipeline that securely exports cleared payroll lines."
      ],
      notes: "This slide outlines the 'Workforce Network OS'. It connects everyone onto the same runtime database rather than maintaining duplicate data silos.",
      element: (
        <div className="p-6 bg-slate-900/60 rounded-xl border border-slate-700/50 space-y-4">
          <h4 className="text-slate-300 font-bold text-center text-sm uppercase tracking-wider">Unified Platform Ecosystem</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700/40 hover:border-teal-500/50 transition-colors">
              <span className="text-teal-400 font-bold text-xs uppercase block mb-1">01. Workforce Demand</span>
              <p className="text-xs text-slate-400">Corporations specify roles, geofences, and open shifts in real time.</p>
            </div>
            <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700/40 hover:border-teal-500/50 transition-colors">
              <span className="text-teal-400 font-bold text-xs uppercase block mb-1">02. Workforce Supply</span>
              <p className="text-xs text-slate-400">Agencies provision validated compliance profiles and manage team assignments.</p>
            </div>
            <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700/40 hover:border-teal-500/50 transition-colors">
              <span className="text-teal-400 font-bold text-xs uppercase block mb-1">03. High-Fidelity Execution</span>
              <p className="text-xs text-slate-400">Workers use quick two-tap geofenced punch interfaces.</p>
            </div>
            <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700/40 hover:border-teal-500/50 transition-colors">
              <span className="text-teal-400 font-bold text-xs uppercase block mb-1">04. Automated Settlement</span>
              <p className="text-xs text-slate-400">Algorithmic payroll locking, automated disputes, direct operational export.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Geofencing Engine",
      subtitle: "Deterministic GPS Validation & Automatic Dwell States",
      topic: "Live Interactive Demo",
      description: "Punches must meet two strict constraints: validated geofence coordinates and a valid active assignment ID. The engine does not simply trust raw GPS [8]; it applies active shift context, assignment validation, timestamp freshness, horizontal accuracy limits, and geofence radius checks.",
      points: [
        "Two-Tap Verification: Punches trigger real-time, 10-meter boundary calculation relative to the target site coordinate.",
        "Server-Side Security Check: Invalid worker coordinates or expired assignment pairings are rejected immediately at the API gate.",
        "Dwell-Time Logic: System tracks presence. If a worker dwells inside or exits a geofence for 10 minutes, the engine logs an audited punch automatically.",
        "Signal Quality Filter: GPS points that are stale (older than 5 minutes), low accuracy (below 150m), or outside boundary are rejected or flagged for review [8]."
      ],
      notes: "Give a live run-through of the geofence engine below! Drag the horizontal slider or click the assignment toggle to see how the platform dynamically allows or denies the punch event.",
      element: (
        <div className="p-6 bg-slate-900/85 rounded-xl border border-teal-500/30 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-slate-200 font-bold text-sm">Interactive GPS Validation Simulator</h4>
            <span className={`px-2 py-0.5 rounded text-2xs font-mono font-bold ${simWorkerDistance <= 10 && simHasJobAssignment ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'}`}>
              {simWorkerDistance <= 10 && simHasJobAssignment ? 'SERVER: VALID' : 'SERVER: INSUFFICIENT'}
            </span>
          </div>

          <div className="bg-slate-950 p-4 rounded-lg space-y-3 border border-slate-800">
            {/* Visual Geofence Circle representation */}
            <div className="h-28 bg-slate-900/60 rounded border border-slate-800 relative overflow-hidden flex items-center justify-center">
              {/* Radius Center Indicator */}
              <div className="absolute w-14 h-14 rounded-full bg-teal-500/15 border border-teal-500/40 flex items-center justify-center animate-pulse">
                <span className="w-2 h-2 rounded-full bg-teal-400"></span>
              </div>
              
              {/* Simulated Worker Indicator */}
              <div 
                className={`absolute transition-all duration-300 flex flex-col items-center justify-center`}
                style={{ 
                  transform: `translateX(${(simWorkerDistance - 10) * 8}px)`, 
                  opacity: 0.9 
                }}
              >
                <div className={`w-4.5 h-4.5 rounded-full flex items-center justify-center text-3xs font-bold ${simWorkerDistance <= 10 ? 'bg-teal-500 text-slate-950 ring-4 ring-teal-500/20' : 'bg-amber-500 text-slate-950 ring-4 ring-amber-500/20'}`}>
                  W
                </div>
                <span className="text-4xs text-slate-400 mt-1 font-mono">{simWorkerDistance}m</span>
              </div>

              <div className="absolute bottom-2 left-2 text-4xs text-slate-500 font-mono">
                Geofence Center (10m Radius)
              </div>
              <div className="absolute bottom-2 right-2 text-4xs text-slate-500 font-mono">
                Worker Offset
              </div>
            </div>

            {/* Simulated UI Controls */}
            <div className="space-y-2">
              <div className="flex flex-col">
                <div className="flex justify-between text-xs text-slate-400 mb-1">
                  <span>Simulate GPS Distance:</span>
                  <span className="font-bold text-teal-400">{simWorkerDistance} meters from site center</span>
                </div>
                <input 
                  type="range" 
                  min="2" 
                  max="25" 
                  value={simWorkerDistance} 
                  onChange={(e) => setSimWorkerDistance(parseInt(e.target.value))}
                  className="w-full accent-teal-400 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-slate-400">Attach Active Assignment:</span>
                <button 
                  onClick={() => setSimHasJobAssignment(!simHasJobAssignment)}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-colors ${simHasJobAssignment ? 'bg-teal-500/15 border-teal-500/40 text-teal-300' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                >
                  {simHasJobAssignment ? 'Active Assignment Linked' : 'No Assignment (Error)'}
                </button>
              </div>
            </div>
          </div>

          <div className="text-2xs text-slate-500 space-y-1 bg-slate-950/40 p-2 rounded border border-slate-800/60 font-mono">
            {simWorkerDistance <= 10 && simHasJobAssignment ? (
              <span className="text-emerald-400">? POST /workers/me/punch-in: 200 OK. Transaction logged to punch_events.</span>
            ) : !simHasJobAssignment ? (
              <span className="text-rose-400">? PUNCH BLOCKED: job_assignment_id parameter missing or invalid. Check active shifts.</span>
            ) : (
              <span className="text-rose-400">? PUNCH BLOCKED: Geofence check failed. Worker is outside safe radius limit.</span>
            )}
          </div>
        </div>
      )
    },
    {
      title: "Secure Multi-Tenant Segregation",
      subtitle: "B2B Trust Bridges & Strict Tenant Filtering",
      topic: "System Security",
      description: "NexusWorkforce360 solves cross-company collaborations without leaking proprietary data. The B2B Trust Bridge is not just access control—it is a compliance boundary that separates tenant data while allowing verified cross-company workforce actions.",
      points: [
        "Global Identities: App user accounts are stored in a main registry, mapping clean, secure credentials.",
        "Hard Tenant Isolations: Daily work sheets, site blueprints, and cost settings enforce constant query checking using target client tokens.",
        "Custom B2B Partnerships: Companies create relationships allowing agencies visibility over specific rosters without revealing internal client tables.",
        "Strict Access Keys: Roles map down to precise permissions like 'tenant.workers.invite' or 'tenant.geofences.manage' for maximum safety."
      ],
      notes: "Explain our strict tenant-isolation code design. Even during complex database joins, we enforce rigorous tenant separation filters to guarantee that records are never compromised.",
      element: (
        <div className="p-6 bg-slate-900/60 rounded-xl border border-slate-700/50 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-slate-300 font-bold text-sm">Interactive Trust Bridge Simulator</h4>
            <div className="flex gap-1">
              <button 
                onClick={() => setSimSelectedRelationship('none')}
                className={`px-2 py-0.5 text-3xs font-semibold rounded border transition-colors ${simSelectedRelationship === 'none' ? 'bg-rose-500/15 border-rose-500/30 text-rose-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}
              >
                No Relation
              </button>
              <button 
                onClick={() => setSimSelectedRelationship('direct')}
                className={`px-2 py-0.5 text-3xs font-semibold rounded border transition-colors ${simSelectedRelationship === 'direct' ? 'bg-amber-500/15 border-amber-500/30 text-amber-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}
              >
                Basic Relation
              </button>
              <button 
                onClick={() => setSimSelectedRelationship('granted')}
                className={`px-2 py-0.5 text-3xs font-semibold rounded border transition-colors ${simSelectedRelationship === 'granted' ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}
              >
                Granted Vis
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-2xs relative">
            <div className="bg-slate-950 p-3 rounded border border-teal-500/30">
              <span className="font-bold text-teal-400 block mb-1">Company Tenant</span>
              <span className="text-slate-500 block">ID: client_101</span>
              <span className="text-slate-500 block">Internal Costing Sheets</span>
            </div>

            <div className="flex items-center justify-center py-2">
              <div className="w-full h-0.5 bg-slate-800 relative flex items-center justify-center">
                <span className={`px-2 py-0.5 rounded text-4xs font-mono font-bold transition-all ${
                  simSelectedRelationship === 'none' ? 'bg-rose-500/25 text-rose-400 border border-rose-500/20' :
                  simSelectedRelationship === 'direct' ? 'bg-amber-500/25 text-amber-400 border border-amber-500/20' :
                  'bg-emerald-500/25 text-emerald-400 border border-emerald-500/20'
                }`}>
                  {simSelectedRelationship === 'none' ? 'LOCKED' : simSelectedRelationship === 'direct' ? 'ROSTER ONLY' : 'TIMESHEETS OPEN'}
                </span>
              </div>
            </div>

            <div className="bg-slate-950 p-3 rounded border border-emerald-500/30">
              <span className="font-bold text-emerald-400 block mb-1">Agency Tenant</span>
              <span className="text-slate-500 block">ID: staff_agency_z</span>
              <span className="text-slate-500 block">Worker Roster Records</span>
            </div>
          </div>

          <div className="text-2xs text-slate-400 font-mono bg-slate-950 p-2.5 rounded border border-slate-800 space-y-1">
            {simSelectedRelationship === 'none' && (
              <p className="text-rose-400">? Security Exception: tenant_id check mismatch. Staffing Agency is blocked from retrieving timesheets and rates for tenant client_101.</p>
            )}
            {simSelectedRelationship === 'direct' && (
              <p className="text-amber-400">? Restricted View: Access is active. Roster allocations match requirements, but detailed rates remain hidden behind tenant restrictions.</p>
            )}
            {simSelectedRelationship === 'granted' && (
              <p className="text-emerald-400">? Complete Trust Bridge Active: Timesheet review permissions are enabled. Timesheets can be processed and verified across companies instantly.</p>
            )}
          </div>
        </div>
      )
    },
    {
      title: "The Frictionless Settlement Pipeline",
      subtitle: "Asymmetric Dispute Loops & Safe Payroll Locks",
      topic: "Core Settlement",
      description: "Reconciling timesheet modifications is a major driver of operational overhead. NexusWorkforce360 reduces dispute cycles by locking evidence before payroll/export, using approved hours, final adjustments, dispute resolution status, and payroll lock state as the clean export boundary [9].",
      points: [
        "Asymmetric Dispute Handling: If a supervisor modifies recorded hours, the affected worker is immediately notified to verify the change.",
        "Worker Dispute Logging: Disputed records capture a direct worker dispute comment, preventing incorrect adjustments from hiding.",
        "Clean Ledger Rules: Billing outputs strictly omit any shift lines flagged with an active disagreement or pending approval.",
        "Instant Export Readiness: Eliminates end-of-month manual spreadsheet comparisons, instantly generating clean financial line records."
      ],
      notes: "Test the payroll engine below! Click on a line status to change its value. Notice how the export counter dynamically reacts, only passing lines that are clean and 'Approved'.",
      element: (
        <div className="p-5 bg-slate-900/85 rounded-xl border border-teal-500/30 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-slate-200 font-bold text-sm">Interactive Settlement Reconciliation Engine</h4>
            <div className="text-right">
              <span className="text-3xs text-slate-500 block">Export Queue Status</span>
              <span className="text-xs font-mono font-bold text-emerald-400">
                {mockTimesheetLines.filter(l => l.status === 'Approved').length} / {mockTimesheetLines.length} Ready to Export
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-2xs text-slate-300 bg-slate-950 rounded-lg overflow-hidden border border-slate-800">
              <thead className="bg-slate-900 text-slate-400 font-semibold uppercase tracking-wider text-3xs border-b border-slate-800">
                <tr>
                  <th className="p-2.5">Worker</th>
                  <th className="p-2.5">Date</th>
                  <th className="p-2.5">Hours</th>
                  <th className="p-2.5 text-center">Status</th>
                  <th className="p-2.5">Dispute File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-mono">
                {mockTimesheetLines.map((line) => (
                  <tr key={line.id} className="hover:bg-slate-900/40">
                    <td className="p-2.5 font-sans font-semibold text-slate-200">{line.worker}</td>
                    <td className="p-2.5 text-slate-400">{line.date}</td>
                    <td className="p-2.5 text-slate-300">{line.activeHours} hrs</td>
                    <td className="p-2.5 text-center">
                      <button 
                        onClick={() => toggleLineStatus(line.id)}
                        className={`px-2 py-0.5 rounded text-4xs font-bold font-mono transition-colors uppercase cursor-pointer border ${
                          line.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          line.status === 'Disputed' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                          'bg-amber-500/10 text-amber-400 border-amber-500/20'
                        }`}
                        title="Click to toggle status simulation"
                      >
                        {line.status}
                      </button>
                    </td>
                    <td className="p-2.5 text-slate-500 truncate max-w-[130px]" title={line.dispute || "None"}>
                      {line.dispute ? (
                        <span className="text-rose-400/90 text-3xs italic flex items-center gap-1">
                          <span className="inline-block w-1 h-1 rounded-full bg-rose-400 animate-ping"></span>
                          {line.dispute}
                        </span>
                      ) : (
                        <span className="text-slate-600 italic">No Active Disputes</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-3xs text-slate-500 italic text-center">
            * Operational Safeguard: Shift lines are automatically locked from payroll export if any worker dispute remains active.
          </p>
        </div>
      )
    },
    {
      title: "An Enterprise-Grade Engine",
      subtitle: "The Production Architecture Blueprint",
      topic: "Tech Infrastructure",
      description: "Our platform is built to replace slow, limited-execution scripting modules. The environment is architected using containerized, high-throughput backend patterns.",
      points: [
        "Highly Scalable Backend: Powered by a fast Fastify API engine using strong type checking.",
        "Reliable PostgreSQL Core: ACID-compliant transaction records handle high-volume event streams seamlessly.",
        "Fully Containerized: Easily deployable across secure, scalable, and isolated server nodes using Docker.",
        "Data Quality Protection: Automated ingestion filters discard inaccurate GPS telemetry, ensuring auto-punches stay reliable."
      ],
      notes: "Present our stack as highly reliable. Explain that shifting off Apps Script to an enterprise design resolves scaling issues, removes runtime constraints, and keeps systems secure.",
      element: (
        <div className="p-6 bg-slate-900/60 rounded-xl border border-slate-700/50 space-y-4">
          <h4 className="text-slate-300 font-bold text-center text-sm uppercase tracking-wider">Enterprise-Grade Stack</h4>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between bg-slate-950 p-2 rounded border border-slate-800">
              <span className="text-xs font-semibold text-slate-300 font-mono">Fastify / TypeScript API Layer</span>
              <span className="text-2xs text-teal-400 font-mono">Node.js Engine</span>
            </div>
            <div className="flex items-center justify-between bg-slate-950 p-2 rounded border border-slate-800">
              <span className="text-xs font-semibold text-slate-300 font-mono">PostgreSQL / Secure Storage</span>
              <span className="text-2xs text-teal-400 font-mono">Multi-Tenant Isolated</span>
            </div>
            <div className="flex items-center justify-between bg-slate-950 p-2 rounded border border-slate-800">
              <span className="text-xs font-semibold text-slate-300 font-mono">Docker Orchestrated System</span>
              <span className="text-2xs text-teal-400 font-mono">Standardized Deployments</span>
            </div>
            <div className="flex items-center justify-between bg-slate-950 p-2 rounded border border-slate-800">
              <span className="text-xs font-semibold text-slate-300 font-mono">Continuous Telemetry Filters</span>
              <span className="text-2xs text-teal-400 font-mono">Precision Filtering</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "ROI with free core operations",
      subtitle: "Unlocking Tangible Operational Returns",
      topic: "Value Assessment",
      description: "Move the workforce slider to estimate recovered payroll leakage, compliance overhead, and admin time savings. Core workforce operations remain free; acceleration and advanced extras are configured separately.",
      points: [
        "Gross savings come from leakage recovery, compliance savings, and admin efficiency.",
        "Core workforce operations have no per-worker or per-location price.",
        "Acceleration, automation, integrations, and advanced extras are optional."
      ],
      notes: "Move the worker count slider to see potential operational savings. Core Workforce Network OS access is Free. Customers pay for faster processing, automation, integrations, advanced capabilities, or enterprise services—not workforce capacity.",
      element: (() => {
        const totalAnnualPayroll = roiInputs.averageAnnualPayrollPerWorker * roiInputs.workerCount;
        const grossMonthlySavings = (totalAnnualPayroll * (roiInputs.payrollLeakageRate + roiInputs.complianceOverheadRate + roiInputs.adminRecoveryRate)) / 12;
        const costInfo = calculateMonthlyCost(roiInputs.workerCount);
        const monthlyPlatformCost = costInfo.totalPrice;
        const netSavings = grossMonthlySavings - monthlyPlatformCost;
        const annualNetSavings = netSavings * 12;
        const savingsToCostRatio = monthlyPlatformCost > 0 ? grossMonthlySavings / monthlyPlatformCost : 0;

        // Payback period calculation
        let paybackDisplay = "Needs more data";
        if (grossMonthlySavings > 0 && monthlyPlatformCost > 0) {
          const paybackDays = (monthlyPlatformCost / grossMonthlySavings) * 30;
          if (paybackDays < 1) {
            paybackDisplay = "< 1 day";
          } else if (paybackDays < 30) {
            paybackDisplay = `${Math.round(paybackDays)} days`;
          } else {
            const months = Math.round(paybackDays / 30 * 10) / 10;
            paybackDisplay = `${months} months`;
          }
        }

        return (
          <div className="p-6 bg-slate-950/40 rounded-xl border border-teal-500/30 shadow-xl space-y-4">
            <div className="space-y-4">
              <h4 className="text-slate-200 font-bold text-sm">Interactive Cost-Benefit Calculator</h4>

              {/* Worker Count Slider Section */}
              <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-800 space-y-2">
                <div className="flex justify-between items-center text-2xs">
                  <span className="text-slate-400 font-semibold">Active Workers</span>
                  <span className="text-teal-400 font-bold text-sm">{roiInputs.workerCount}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="1500"
                  value={roiInputs.workerCount}
                  onChange={(e) => setRoiInputs({...roiInputs, workerCount: parseInt(e.target.value)})}
                  className="w-full accent-teal-400 h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                />
                <div className="text-3xs text-slate-400 space-y-0.5">
                  <div><span className="font-semibold">{roiInputs.workerCount} active workers → {costInfo.plan.name} plan</span></div>
                  <div className="text-slate-500">Lowest plan that includes this workforce size</div>
                </div>
              </div>

              {/* Annual Payroll Input */}
              <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-800 space-y-2">
                <div className="flex justify-between items-center text-2xs">
                  <span className="text-slate-400 font-semibold">Annual Payroll per Worker</span>
                  <input
                    type="number"
                    value={roiInputs.averageAnnualPayrollPerWorker}
                    onChange={(e) => setRoiInputs({...roiInputs, averageAnnualPayrollPerWorker: parseFloat(e.target.value) || 45000})}
                    className="w-20 px-2 py-1 bg-slate-950 border border-slate-700 rounded text-xs text-teal-400 font-mono text-right"
                  />
                </div>
                <span className="text-2xs text-slate-400 font-mono">
                  Total payroll: ${totalAnnualPayroll.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </span>
              </div>

              {/* Savings Components (minimal, collapsed) */}
              <div className="grid grid-cols-3 gap-1.5 text-2xs">
                <div className="bg-slate-900/60 p-1.5 rounded border border-emerald-500/20">
                  <span className="text-emerald-400 font-bold text-xs block">${(totalAnnualPayroll * roiInputs.payrollLeakageRate / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  <span className="text-3xs text-slate-500">Leakage</span>
                </div>
                <div className="bg-slate-900/60 p-1.5 rounded border border-sky-500/20">
                  <span className="text-sky-400 font-bold text-xs block">${(totalAnnualPayroll * roiInputs.complianceOverheadRate / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  <span className="text-3xs text-slate-500">Compliance</span>
                </div>
                <div className="bg-slate-900/60 p-1.5 rounded border border-amber-500/20">
                  <span className="text-amber-400 font-bold text-xs block">${(totalAnnualPayroll * roiInputs.adminRecoveryRate / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  <span className="text-3xs text-slate-500">Admin</span>
                </div>
              </div>

              {/* Primary Results (Main three metrics) */}
              <div className="space-y-2 bg-slate-950/40 p-2.5 rounded-lg border border-slate-800/50">
                {/* Gross Monthly Savings */}
                <div className="bg-emerald-500/10 p-2.5 rounded-lg border border-emerald-500/30">
                  <span className="text-3xs text-emerald-400 font-semibold block mb-0.5">Gross monthly savings</span>
                  <span className="text-emerald-300 font-bold text-lg block font-mono">
                    ${grossMonthlySavings.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>

                {/* Platform Cost */}
                <div className="bg-slate-900/80 p-2.5 rounded-lg border border-slate-700">
                  <span className="text-3xs text-slate-400 font-semibold block mb-1">NexusWorkforce360 cost</span>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-bold text-slate-300 font-mono">${monthlyPlatformCost.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    <span className="text-3xs text-slate-500">/mo</span>
                  </div>
                  <div className="text-3xs text-slate-500 space-y-0.5 border-t border-slate-700 pt-1.5">
                    <div className="flex justify-between">
                      <span>{costInfo.plan.name} plan</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Core operations:</span>
                      <span className="text-slate-400">No per-worker pricing</span>
                    </div>
                    <div className="flex justify-between text-teal-400/80"><span>Acceleration:</span><span>Optional</span></div>
                  </div>
                </div>

                {/* Net Savings - The Main Result */}
                <div className={`p-3 rounded-lg border-2 ${netSavings >= 0 ? 'bg-teal-500/15 border-teal-500/50' : 'bg-amber-500/15 border-amber-500/50'}`}>
                  <div className="flex justify-between items-baseline">
                    <span className={`text-3xs font-semibold block ${netSavings >= 0 ? 'text-teal-400' : 'text-amber-400'}`}>Net savings after platform cost</span>
                  </div>
                  <span className={`font-bold text-2xl block font-mono mt-1 ${netSavings >= 0 ? 'text-teal-300' : 'text-amber-300'}`}>
                    {netSavings >= 0 ? '$' : ''}
                    {Math.abs(netSavings).toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                  <span className="text-3xs text-slate-500 mt-1 block italic">per month</span>
                </div>

                {/* Calculation Explanation */}
                <div className="text-3xs text-slate-500 border-t border-slate-700 pt-2 text-center">
                  <span className="block">Net savings = gross savings − NexusWorkforce360 cost</span>
                </div>
              </div>

              {/* Secondary Results */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2.5 rounded-lg border ${netSavings >= 0 ? 'bg-teal-500/10 border-teal-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                  <span className={`text-3xs font-semibold block mb-0.5 ${netSavings >= 0 ? 'text-teal-400' : 'text-amber-400'}`}>Projected annual</span>
                  <span className={`font-bold text-lg block font-mono ${netSavings >= 0 ? 'text-teal-300' : 'text-amber-300'}`}>
                    ${Math.abs(annualNetSavings).toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                  <span className="text-3xs text-slate-500 mt-0.5">net savings</span>
                </div>

                <div className={`p-2.5 rounded-lg border ${savingsToCostRatio >= 1 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                  <span className={`text-3xs font-semibold block mb-0.5 ${savingsToCostRatio >= 1 ? 'text-emerald-400' : 'text-amber-400'}`}>Savings-to-cost</span>
                  <span className={`font-bold text-lg block font-mono ${savingsToCostRatio >= 1 ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {savingsToCostRatio.toFixed(2)}x
                  </span>
                  <span className="text-3xs text-slate-500 mt-0.5">ratio</span>
                </div>

                <div className="p-2.5 rounded-lg border bg-slate-900/60 border-slate-700 col-span-2">
                  <span className="text-3xs text-slate-400 font-semibold block mb-0.5">Payback period</span>
                  <span className="font-bold text-base block font-mono text-slate-300">
                    {paybackDisplay}
                  </span>
                </div>
              </div>

              {/* Adjustment Sliders (collapsed) */}
              <details className="text-3xs">
                <summary className="text-slate-400 font-semibold cursor-pointer hover:text-slate-300">Adjust recovery assumptions</summary>
                <div className="mt-2 space-y-2">
                  <div className="bg-slate-900/40 p-2 rounded border border-slate-800/50">
                    <div className="flex justify-between mb-1 text-2xs">
                      <span className="text-slate-400">Payroll Leakage</span>
                      <span className="text-emerald-400 font-mono">{(roiInputs.payrollLeakageRate * 100).toFixed(1)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.01"
                      max="0.05"
                      step="0.001"
                      value={roiInputs.payrollLeakageRate}
                      onChange={(e) => setRoiInputs({...roiInputs, payrollLeakageRate: parseFloat(e.target.value)})}
                      className="w-full h-1 bg-slate-800 rounded accent-emerald-400"
                    />
                  </div>

                  <div className="bg-slate-900/40 p-2 rounded border border-slate-800/50">
                    <div className="flex justify-between mb-1 text-2xs">
                      <span className="text-slate-400">Compliance Overhead</span>
                      <span className="text-sky-400 font-mono">{(roiInputs.complianceOverheadRate * 100).toFixed(1)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.01"
                      max="0.08"
                      step="0.001"
                      value={roiInputs.complianceOverheadRate}
                      onChange={(e) => setRoiInputs({...roiInputs, complianceOverheadRate: parseFloat(e.target.value)})}
                      className="w-full h-1 bg-slate-800 rounded accent-sky-400"
                    />
                  </div>

                  <div className="bg-slate-900/40 p-2 rounded border border-slate-800/50">
                    <div className="flex justify-between mb-1 text-2xs">
                      <span className="text-slate-400">Admin Overhead</span>
                      <span className="text-amber-400 font-mono">{(roiInputs.adminRecoveryRate * 100).toFixed(2)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.002"
                      max="0.03"
                      step="0.001"
                      value={roiInputs.adminRecoveryRate}
                      onChange={(e) => setRoiInputs({...roiInputs, adminRecoveryRate: parseFloat(e.target.value)})}
                      className="w-full h-1 bg-slate-800 rounded accent-amber-400"
                    />
                  </div>
                </div>
              </details>

              {/* Pricing Model Note */}
              <div className="bg-slate-900/40 p-2.5 rounded-lg border border-slate-800 text-3xs text-slate-400">
                <p className="text-slate-300 font-semibold mb-1">Pricing Note:</p>
                <p>Core Workforce Network OS access is Free. Paid value comes from acceleration, automation, priority processing, integrations, advanced capabilities, and services.</p>
              </div>
            </div>
          </div>
        );
      })()
    }
  ];

  // Auto-play loop logic
  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000); // 7 seconds per slide

    return () => {
      window.clearInterval(interval);
    };
  }, [isPlaying, slides.length]);

  // Keyboard Navigation Support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      } else if (e.key === 'ArrowLeft') {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleCopyNotes = (text: string, idx: number) => {
    // Standard execution of document.execCommand fallback for environment safety
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
      setCopiedIndex(idx);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.warn('Unable to copy using execCommand fallback', err);
    }
    document.body.removeChild(el);
  };

  const activeSlide = slides[currentSlide];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans flex flex-col selection:bg-teal-500 selection:text-slate-950">
      
      {/* Dynamic Upper Presentation Ribbon */}
      <header className="border-b border-slate-800/80 bg-slate-900/60 backdrop-blur-md sticky top-0 z-50 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-teal-500 to-emerald-400 flex items-center justify-center shadow-md shadow-teal-500/10">
            <svg className="w-5 h-5 text-slate-950" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-base font-extrabold tracking-tight text-slate-100 flex items-center gap-2">
              NexusWorkforce360 <span className="text-3xs font-mono font-normal bg-teal-500/10 text-teal-400 border border-teal-500/20 px-1.5 py-0.5 rounded">v2.0 OS</span>
            </h1>
            <p className="text-4xs text-slate-400 font-mono tracking-wider uppercase">Interactive Network Sales Deck & Blueprint</p>
          </div>
        </div>

        {/* Global Slide Control Deck Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Back background switcher */}
          <button
            onClick={() => setSlideBgStyle(prev => prev === 'dark' ? 'light' : 'dark')}
            className="p-1.5 rounded-lg border border-slate-800 hover:border-slate-700 bg-slate-950/80 hover:bg-slate-900 text-slate-400 hover:text-slate-200 transition-all flex items-center gap-1.5 text-xs font-semibold"
            title="Toggle Slide Backlighting Style"
          >
            {slideBgStyle === 'dark' ? <IconSun /> : <IconMoon />}
            <span className="hidden md:inline">{slideBgStyle === 'dark' ? 'Light Theme' : 'Dark Theme'}</span>
          </button>

          {/* Sources and Citations Side panel toggle */}
          <button
            onClick={() => {
              setShowSources(prev => !prev);
              if (showNotes) setShowNotes(false);
            }}
            className={`p-1.5 rounded-lg border transition-all flex items-center gap-1.5 text-xs font-semibold ${
              showSources 
                ? 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/25' 
                : 'bg-slate-950/80 border-slate-800 text-slate-400 hover:bg-slate-900 hover:text-slate-200'
            }`}
            title="Show Verified Research Sources Registry"
          >
            <IconBookOpen />
            <span className="hidden md:inline">{showSources ? 'Hide Sources' : 'View Sources [4]'}</span>
          </button>

          {/* Slide notes visibility switcher */}
          <button
            onClick={() => {
              setShowNotes(prev => !prev);
              if (showSources) setShowSources(false);
            }}
            className={`p-1.5 rounded-lg border transition-all flex items-center gap-1.5 text-xs font-semibold ${
              showNotes 
                ? 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/25' 
                : 'bg-slate-950/80 border-slate-800 text-slate-400 hover:bg-slate-900 hover:text-slate-200'
            }`}
            title="Toggle Presenter Script Side Drawer"
          >
            <IconNotes />
            <span className="hidden md:inline">{showNotes ? 'Hide Notes' : 'Show Notes'}</span>
          </button>

          {/* Autoplay toggles */}
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`p-1.5 rounded-lg border transition-all flex items-center gap-1.5 text-xs font-semibold ${
              isPlaying 
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25' 
                : 'bg-slate-950/80 border-slate-800 text-slate-400 hover:bg-slate-900'
            }`}
          >
            {isPlaying ? <IconPause /> : <IconPlay />}
            <span className="hidden md:inline">{isPlaying ? 'Autoplay On' : 'Autoplay'}</span>
          </button>

          <div className="w-px h-5 bg-slate-800 hidden sm:block"></div>

          {/* Quick PDF-Style raw script copy utility */}
          <button 
            onClick={() => handleCopyNotes(activeSlide.notes, currentSlide)}
            className="p-1.5 rounded-lg border border-slate-800 hover:border-slate-700 bg-slate-950/80 hover:bg-slate-900 text-slate-400 hover:text-slate-200 transition-all flex items-center gap-1.5 text-xs font-semibold"
            title="Copy Talking Points For This Slide"
          >
            {copiedIndex === currentSlide ? <IconCheck /> : <IconPresentation />}
            <span className="hidden md:inline">{copiedIndex === currentSlide ? 'Script Copied' : 'Copy Script'}</span>
          </button>
        </div>
      </header>

      {/* Main Structural Framework Side-by-Side Area */}
      <div className="flex-1 flex flex-col xl:flex-row overflow-hidden relative">
        
        {/* Left Hand Navigation Panel: Quick Access Grid */}
        <aside className="w-full xl:w-72 border-r border-b xl:border-b-0 border-slate-800/80 bg-slate-950 p-4 shrink-0 space-y-3">
          <h2 className="text-3xs uppercase tracking-wider text-slate-500 font-mono font-bold">Presentation Progress ({slides.length} slides)</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-1 gap-2">
            {slides.map((slide, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`text-left p-2.5 rounded-lg border transition-all flex flex-col space-y-1 relative group overflow-hidden ${
                  currentSlide === index 
                    ? 'bg-gradient-to-r from-teal-950/40 to-slate-900 border-teal-500/50 text-slate-100 ring-1 ring-teal-500/20 shadow-md shadow-teal-500/5' 
                    : 'bg-slate-900/40 border-slate-850 hover:bg-slate-900/80 hover:border-slate-750 text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center justify-between gap-1 w-full">
                  <span className={`text-4xs font-mono font-semibold uppercase tracking-wider ${currentSlide === index ? 'text-teal-400' : 'text-slate-500'}`}>
                    Slide 0{index + 1} � {slide.topic}
                  </span>
                  {currentSlide === index && <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>}
                </div>
                <span className="text-xs font-bold leading-tight line-clamp-1">{slide.title}</span>
                <span className="text-4xs text-slate-500 font-sans line-clamp-1">{slide.subtitle}</span>
                
                {/* Visual completion tracker bar for autoplay state */}
                {currentSlide === index && isPlaying && (
                  <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-400 animate-slideProgress"></div>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Central Core Presentation Stage */}
        <main className="flex-1 p-4 md:p-8 flex items-center justify-center relative overflow-y-auto">
          
          <div className="w-full max-w-5xl h-full flex flex-col justify-between space-y-6">
            
            {/* Visual Backlit Card Border Frame */}
            <div className={`flex-1 rounded-2xl border transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${
              slideBgStyle === 'dark' 
                ? 'bg-slate-950 border-slate-800 text-slate-100 shadow-2xl shadow-slate-950/80' 
                : 'bg-slate-50 border-slate-300 text-slate-950 shadow-xl shadow-slate-200/50'
            }`}>
              
              {/* Outer visual layout design lines */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-500 via-emerald-400 to-teal-600"></div>
              
              <div className="p-6 md:p-10 flex-1 flex flex-col lg:flex-row gap-8 justify-between items-stretch">
                
                {/* Left Area: Explanatory Content Block */}
                <div className="flex-1 flex flex-col justify-between space-y-6 lg:max-w-xl">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 rounded text-4xs font-mono font-extrabold uppercase tracking-widest bg-teal-500/10 text-teal-400 border border-teal-500/25">
                        {activeSlide.topic}
                      </span>
                      <span className={`text-4xs font-mono tracking-wider ${slideBgStyle === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                        Slide 0{currentSlide + 1} of 0{slides.length}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      <h2 className="text-2xl md:text-3xl font-black tracking-tight">{activeSlide.title}</h2>
                      <p className={`text-sm md:text-base font-semibold leading-relaxed ${slideBgStyle === 'dark' ? 'text-teal-400' : 'text-teal-700'}`}>
                        {activeSlide.subtitle}
                      </p>
                    </div>

                    <p className={`text-xs md:text-sm leading-relaxed ${slideBgStyle === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {activeSlide.description}
                    </p>

                    <ul className="space-y-2.5 pt-2">
                      {activeSlide.points.map((pt, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0"></span>
                          <span className={slideBgStyle === 'dark' ? 'text-slate-300' : 'text-slate-700'}>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Operational Safe Guard Indicator Info Footer */}
                  <div className={`text-4xs font-mono flex items-center gap-1.5 border-t py-3 ${slideBgStyle === 'dark' ? 'border-slate-900 text-slate-500' : 'border-slate-200 text-slate-400'}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse"></span>
                    NexusWorkforce360 Core Network Framework Engine � Confidential Presentation System v2.0
                  </div>
                </div>

                {/* Right Area: Dynamic Simulated Interactive Graphics Panel */}
                <div className="flex-1 min-h-[300px] flex items-center justify-center lg:border-l lg:pl-8 border-slate-800/80">
                  <div className="w-full max-w-md">
                    {activeSlide.element}
                  </div>
                </div>

              </div>

              {/* Lower Slide Quick Command Buttons */}
              <div className={`px-6 py-4 border-t flex items-center justify-between gap-4 ${slideBgStyle === 'dark' ? 'border-slate-900 bg-slate-950/60' : 'border-slate-200 bg-slate-100/50'}`}>
                
                <div className="flex items-center gap-1">
                  <span className={`text-3xs font-mono ${slideBgStyle === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>Keyboard Controls: Use Left / Right arrows to navigate</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevSlide}
                    className={`p-2 rounded-xl border transition-all flex items-center justify-center ${
                      slideBgStyle === 'dark' 
                        ? 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800 hover:text-slate-200 hover:border-slate-700' 
                        : 'bg-white border-slate-350 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                    title="Previous Slide"
                  >
                    <IconChevronLeft />
                  </button>
                  
                  <span className={`text-xs font-mono font-bold px-3 ${slideBgStyle === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                    0{currentSlide + 1} / 0{slides.length}
                  </span>

                  <button
                    onClick={handleNextSlide}
                    className={`p-2 rounded-xl border transition-all flex items-center justify-center ${
                      slideBgStyle === 'dark' 
                        ? 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800 hover:text-slate-200 hover:border-slate-700' 
                        : 'bg-white border-slate-350 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                    title="Next Slide"
                  >
                    <IconChevronRight />
                  </button>
                </div>

              </div>

            </div>

          </div>
        </main>

        {/* Dynamic Context Right Sidebar Drawer: Notes or Verified Sources Panel */}
        {(showNotes || showSources) && (
          <aside className="w-full xl:w-80 border-t xl:border-t-0 xl:border-l border-slate-800/80 bg-slate-950/90 backdrop-blur-sm p-5 shrink-0 flex flex-col justify-between space-y-4">
            
            {/* VIEW A: Verified Sources and Research Panel */}
            {showSources && (
              <div className="space-y-3 flex-1 overflow-y-auto flex flex-col">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                  <span className="text-3xs uppercase tracking-wider text-teal-400 font-mono font-bold flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></span> Research Sources & Assumptions
                  </span>
                  <button
                    onClick={() => setShowSources(false)}
                    className="text-4xs text-slate-500 hover:text-slate-300 uppercase font-mono font-bold"
                  >
                    [Dismiss]
                  </button>
                </div>

                <div className="space-y-3 pr-1 flex-1 overflow-y-auto">
                  {industrySources.map((src) => (
                    <div key={src.id} className="p-3 bg-slate-900/60 rounded-lg border border-slate-800 space-y-1.5 hover:border-teal-500/20 transition-all">
                      <div className="flex items-center justify-between">
                        <span className="text-3xs font-mono font-bold text-teal-400 bg-teal-500/5 px-1.5 py-0.5 rounded">
                          Source {src.ref}
                        </span>
                        {src.url && (
                          <a
                            href={src.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-4xs text-slate-500 hover:text-teal-400 underline font-mono"
                          >
                            Visit Authority
                          </a>
                        )}
                      </div>
                      <h4 className="text-xs font-bold text-slate-200 font-sans">{src.authority}</h4>
                      <p className="text-3xs text-slate-400 leading-normal font-sans italic">
                        "{src.finding}"
                      </p>
                    </div>
                  ))}
                </div>

                {/* Caveat about benchmark assumptions */}
                <div className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/20 mt-2">
                  <h5 className="text-3xs uppercase font-mono font-bold text-amber-400 mb-1.5">Benchmark Assumptions Caveat</h5>
                  <p className="text-3xs text-amber-300/90 leading-normal">
                    Some model inputs are configurable benchmark assumptions until the source artifact is attached. Replace assumptions with customer-specific baseline data during discovery.
                  </p>
                </div>
              </div>
            )}

            {/* VIEW B: Presenter Action Talking Points */}
            {showNotes && (
              <div className="space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                  <span className="text-3xs uppercase tracking-wider text-slate-400 font-mono font-bold flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-teal-400"></span> Presenter Talking Points
                  </span>
                  <button 
                    onClick={() => setShowNotes(false)}
                    className="text-4xs text-slate-500 hover:text-slate-300 uppercase font-mono font-bold"
                  >
                    [Dismiss]
                  </button>
                </div>

                <div className="p-3.5 bg-slate-900/70 rounded-lg border border-slate-850 relative overflow-hidden space-y-3">
                  <div className="absolute top-0 right-0 p-1 font-mono text-3xs text-slate-600">SLIDE_TALK_M_v2</div>
                  
                  <h4 className="text-xs font-bold text-teal-400 font-mono">Core Theme:</h4>
                  <p className="text-2xs text-slate-300 leading-relaxed italic">
                    "{activeSlide.notes}"
                  </p>
                  
                  <div className="pt-2 border-t border-slate-850 space-y-2">
                    <h5 className="text-3xs uppercase font-mono font-bold text-slate-400">Key Takeaway Highlight:</h5>
                    <p className="text-3xs text-slate-400 leading-relaxed">
                      Make sure to focus on structural scaling. Frame NexusWorkforce360 as the foundational relational layer for B2B workforce exchanges, leaving legacy, limited execution scripts completely behind.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-slate-900/40 p-3 rounded-lg border border-slate-850 space-y-2.5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                <span className="text-4xs font-mono font-bold text-slate-400 uppercase tracking-wide">Developer System Health</span>
              </div>
              <div className="space-y-1 font-mono text-4xs text-slate-500">
                <div>� Fastify Server Gateways: <span className="text-emerald-400">ONLINE</span></div>
                <div>� PostgreSQL Storage Partition: <span className="text-emerald-400">ISOLATED</span></div>
                <div>� Active Network Swarm: <span className="text-emerald-400">ACTIVE</span></div>
                <div>� Docker Deploy Environment: <span className="text-emerald-400">VERIFIED</span></div>
              </div>
            </div>

          </aside>
        )}

      </div>

      {/* Global CSS Style tag for dynamic UI animations (slide completions, custom transitions, slide bars) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
        @keyframes slideProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-slideProgress {
          animation: slideProgress 7s linear forwards;
        }
        .text-3xs {
          font-size: 0.65rem;
        }
        .text-4xs {
          font-size: 0.58rem;
        }
        .text-2xs {
          font-size: 0.72rem;
        }
        .text-3xs {
          font-size: 0.65rem;
        }
        .font-sans {
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        .border-slate-850 {
          border-color: rgba(30, 41, 59, 0.7);
        }
      `}</style>

    </div>
  );
}
export default InteractiveSalesDeck;
